import React from "react";

const MinimalistElegant = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        dueDate,
        invoiceItems,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-white p-20 font-serif text-slate-700 leading-relaxed min-h-[1100px] flex flex-col justify-between border-[20px] border-slate-50">
            <div>
                {/* Header */}
                <div className="flex justify-between items-start mb-24">
                    <div className="space-y-2">
                        <div className="text-sm font-black uppercase tracking-[0.4em] text-slate-300">Invoice From</div>
                        <h1 className="text-3xl font-black tracking-tighter text-slate-900 uppercase italic">{companyDetails?.name}</h1>
                        <p className="text-[10px] uppercase tracking-widest text-slate-400 max-w-[200px] leading-loose">{companyDetails?.address}</p>
                    </div>

                    <div className="text-right space-y-4">
                        <div className="text-6xl font-black text-slate-50 uppercase tracking-tighter leading-none mb-4 italic">BILL</div>
                        <div className="space-y-1">
                            <div className="text-xs font-black text-slate-900 uppercase tracking-widest"># {title}</div>
                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.3em]">Issued: {date}</div>
                        </div>
                    </div>
                </div>

                {/* Receiver Info */}
                <div className="mb-24 flex justify-between items-end border-b border-slate-100 pb-16">
                    <div className="space-y-4">
                        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300">Recipient</div>
                        <div className="space-y-1">
                            <div className="text-2xl font-black text-slate-900 tracking-tighter">{to}</div>
                            <div className="text-sm font-medium text-slate-500">{clientAddress}</div>
                        </div>
                    </div>
                    <div className="text-right space-y-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        <div className="items-center flex gap-2">Due Date</div>
                        <div className="text-slate-900 font-bold border-b border-slate-200 pb-1 italic">{dueDate}</div>
                    </div>
                </div>

                {/* Table */}
                <div className="mb-16">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300 border-b border-slate-100 italic">
                                <th className="py-6 pr-4">Description</th>
                                <th className="py-6 text-center px-4">Qty</th>
                                <th className="py-6 text-right px-4">Rate</th>
                                <th className="py-6 text-right pl-4">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 border-b border-slate-50">
                            {(invoiceItems || []).map((item, idx) => (
                                <tr key={idx}>
                                    <td className="py-10 pr-4">
                                        <div className="text-xl font-black text-slate-900 tracking-tighter italic uppercase">{item.item}</div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-[0.2em] mt-2 italic font-bold">Ref Code: ITEM-{idx + 101}</div>
                                    </td>
                                    <td className="py-10 text-center text-xs font-bold text-slate-300 italic">{item.qty}</td>
                                    <td className="py-10 text-right text-xs font-black text-slate-900 uppercase tracking-tighter italic">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                    <td className="py-10 text-right text-2xl font-black text-slate-900 uppercase tracking-tighter italic pl-6">Nu. {parseFloat(item.amount).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Bottom Section */}
            <div>
                <div className="flex justify-between items-start mb-16">
                    <div className="max-w-[400px] space-y-6">
                        <div className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-200">Notes & Protocol</div>
                        <p className="text-[10px] font-bold leading-relaxed text-slate-400 uppercase tracking-widest italic border-l-2 border-slate-100 pl-6">
                            {notes || "We appreciate your professional collaboration. This document serves as a formal request for settlement of services rendered."}
                            <br /><br />
                            {terms || "Settlement required within professional term limits. Late adjustments subject to operational protocols."}
                        </p>
                    </div>

                    <div className="w-72 space-y-6">
                        <div className="flex justify-between text-[11px] font-black text-slate-300 uppercase tracking-[0.3em] italic">
                            <span>Subtotal</span>
                            <span className="text-slate-900">Nu. {parseFloat(subTotal).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-[11px] font-black text-slate-300 uppercase tracking-[0.3em] italic">
                            <span>Tax ({taxRate}%)</span>
                            <span className="text-slate-900">Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-slate-400 italic border-b border-slate-50 pb-2">
                                <span>Discount</span>
                                <span>- Nu. {parseFloat(discount).toLocaleString()}</span>
                            </div>
                        )}
                        <div className="pt-8 mt-8 border-t border-slate-900 flex justify-between items-end">
                            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-900">Total</span>
                            <div className="text-4xl font-black text-slate-900 tracking-tighter italic leading-none">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                        </div>
                    </div>
                </div>

                {/* Banking & Signed */}
                <div className="flex justify-between items-end border-t border-slate-100 pt-10">
                    <div className="space-y-2">
                        <div className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-200">Electronic Transfer</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            {companyDetails?.bankName} — {companyDetails?.accountNo}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-200 mb-6">Signature & Stamp</div>
                        <div className="w-40 h-px bg-slate-100"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MinimalistElegant;
